import { unwrap } from "@majourneys/api-client";
import type { Activity } from "@majourneys/shared";
import { api } from "../../../lib/api";

export type SavedItem = {
  id: string;
  slug: string;
  title: string;
  city: string;
  price: number;
  currency: string;
  priceType?: string | number;
  image?: string;
  category?: string;
  rating?: number;
  /** What the place is: a restaurant or an editorial listing shows no price. */
  mode?: string;
  categorySlug?: string | null;
  segment?: string | null;
};

/** One row of GET /me/saved, the paged list the app reads too. */
type SavedRow = Activity & { segment?: string | null };

type SavedPage = {
  data?: SavedRow[];
  meta?: { nextCursor?: string | null };
  /** The old website-only shape, still read so a stale API cannot empty the list. */
  items?: SavedItem[];
};

type SavedGet = (o: { query: Record<string, string> }) => Promise<{ data: unknown; error: unknown }>;

/** The most the server returns per page. */
const PAGE_SIZE = 100;
/** Enough for 1,000 hearts; a cursor that never ends must not loop forever. */
const MAX_PAGES = 10;

export function toSavedItem(row: SavedRow): SavedItem {
  return {
    id: row.id,
    slug: row.slug,
    title: row.title,
    city: row.city?.name ?? "",
    price: row.price,
    currency: row.currency,
    priceType: row.priceType,
    image: row.image ?? undefined,
    category: row.category?.name || undefined,
    rating: row.rating ?? undefined,
    mode: row.mode ?? undefined,
    categorySlug: row.categorySlug ?? null,
    segment: row.segment ?? null,
  };
}

export const savedApi = {
  /**
   * Everything the account has saved, newest first.
   *
   * GET /me/saved answers `{ data, meta }` and pages. This used to read
   * `.items`, the shape of a second GET /me/saved handler that only in-memory
   * tests ever reached; the live server answered with this one, so the list
   * always came back empty and a saved heart looked lost after a reload.
   */
  listMine: async (): Promise<SavedItem[]> => {
    const saved = api.me.saved as unknown as { get: SavedGet };
    const out: SavedItem[] = [];
    let cursor: string | undefined;
    for (let page = 0; page < MAX_PAGES; page++) {
      const query: Record<string, string> = { limit: String(PAGE_SIZE), sort: "recent" };
      if (cursor) query.cursor = cursor;
      const res = unwrap(await saved.get({ query })) as SavedPage | null;
      if (Array.isArray(res?.items)) return res.items;
      out.push(...(res?.data ?? []).map(toSavedItem));
      cursor = res?.meta?.nextCursor ?? undefined;
      if (!cursor) break;
    }
    return out;
  },
};
